// The email delivery trail — "was an email sent for this, and if not why".
//
// It exists because that question had no answer from inside the app. A
// successful send left a dedup marker, a suppressed one left a log line and a
// failed one left a different log line, so a manager who never heard about a
// leave request could not be told which had happened — and logs roll.
//
// The most important thing on this screen is the ENVIRONMENT banner. Outside
// production, mail to anyone not on the allowlist is suppressed on purpose, so
// a wall of "suppressed" rows is the guard working, not an outage. Without
// saying so, this page would generate exactly the false alarm it was built to
// prevent.
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { AlertTriangle, CheckCircle2, Info, Loader2, Mail, MinusCircle, RefreshCw } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/features/shared/components/ui/card';
import { Button } from '@/features/shared/components/ui/button';
import { Input } from '@/features/shared/components/ui/input';
import { Badge } from '@/features/shared/components/ui/badge';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '@/features/shared/components/ui/select';
import { getApiErrorMessage } from '@/lib/apiError';
import { formatDateTime } from '@/lib/orgFormat';
import {
  getEmailDeliveries, type EmailDelivery, type EmailDeliveryStatus,
} from '@/services/notificationPreferencesService';

const STATUS_META: Record<EmailDeliveryStatus, { label: string; hint: string; icon: React.ElementType; cls: string }> = {
  sent: {
    label: 'Sent',
    hint: 'Handed to the mail provider, which accepted it.',
    icon: CheckCircle2,
    cls: 'border-success/40 text-success',
  },
  failed: {
    label: 'Failed',
    hint: 'The provider refused it, or we could not reach them.',
    icon: AlertTriangle,
    cls: 'border-danger/40 text-danger',
  },
  suppressed: {
    label: 'Suppressed',
    hint: 'Deliberately not sent — the recipient is not on this environment’s allowlist.',
    icon: MinusCircle,
    cls: 'border-warning/40 text-warning',
  },
  deduped: {
    label: 'Already sent',
    hint: 'A repeat of an event that had already delivered this email, so it was not sent twice.',
    icon: Info,
    cls: 'border-border text-muted-foreground',
  },
};

const STATUSES: EmailDeliveryStatus[] = ['sent', 'failed', 'suppressed', 'deduped'];

export const EmailDeliveryTrailCard: React.FC = () => {
  const { t } = useTranslation();
  const [rows, setRows] = useState<EmailDelivery[]>([]);
  const [environment, setEnvironment] = useState('');
  const [recipient, setRecipient] = useState('');
  const [status, setStatus] = useState<EmailDeliveryStatus | 'all'>('all');
  const [loading, setLoading] = useState(true);
  // 403 = not a tenant admin. The card hides rather than showing an error: an
  // employee has no business seeing who was emailed, and a permission notice
  // in Settings reads as something broken.
  const [forbidden, setForbidden] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const d = await getEmailDeliveries({
        recipient: recipient.trim() || undefined,
        status: status === 'all' ? undefined : status,
      });
      setRows(d.deliveries);
      setEnvironment(d.environment);
      setForbidden(false);
    } catch (err) {
      const code = (err as { response?: { status?: number } })?.response?.status;
      if (code === 403 || code === 401) { setForbidden(true); return; }
      toast.error(getApiErrorMessage(err, t('emailTrail.loadFailed', {
        defaultValue: 'Could not load the email delivery trail.',
      })));
    } finally {
      setLoading(false);
    }
  }, [recipient, status, t]);

  useEffect(() => { void load(); }, [load]);

  if (forbidden) return null;

  const nonProd = !!environment && environment.toLowerCase() !== 'production';

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between gap-2">
        <CardTitle className="flex items-center gap-2 text-sm font-medium">
          <Mail className="h-4 w-4 text-muted-foreground" aria-hidden />
          {t('emailTrail.title', { defaultValue: 'Email delivery' })}
        </CardTitle>
        <Button size="sm" variant="ghost" onClick={() => void load()} disabled={loading}
          aria-label={t('emailTrail.refresh', { defaultValue: 'Refresh' })}>
          {loading ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                   : <RefreshCw className="h-4 w-4" aria-hidden />}
        </Button>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-xs text-muted-foreground">
          {t('emailTrail.help', {
            defaultValue:
              'Every email this platform tried to send for your company — including the ones it deliberately did not send, and why. Message contents are never recorded.',
          })}
        </p>

        {nonProd && (
          <div className="flex items-start gap-2 rounded-md border border-warning/40 bg-warning/10 p-3 text-xs text-foreground">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-warning" aria-hidden />
            <span>
              {t('emailTrail.nonProd', {
                defaultValue:
                  'This is the {{env}} environment, so mail to anyone outside the allowed list is suppressed on purpose. “Suppressed” here means we chose not to send — not that sending is broken.',
                env: environment,
              })}
            </span>
          </div>
        )}

        <div className="flex flex-wrap items-end gap-2">
          <div>
            <label className="text-xs text-muted-foreground" htmlFor="trail-recipient">
              {t('emailTrail.recipient', { defaultValue: 'Recipient' })}
            </label>
            <Input
              id="trail-recipient"
              className="h-9 w-[18rem]"
              value={recipient}
              onChange={(e) => setRecipient(e.target.value)}
              placeholder={t('emailTrail.recipientPh', { defaultValue: 'name@company.com' })}
            />
          </div>
          <div>
            <label className="text-xs text-muted-foreground" htmlFor="trail-status">
              {t('emailTrail.status', { defaultValue: 'Outcome' })}
            </label>
            <Select value={status} onValueChange={(v) => setStatus(v as EmailDeliveryStatus | 'all')}>
              <SelectTrigger id="trail-status" className="h-9 w-[12rem]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('emailTrail.any', { defaultValue: 'Any outcome' })}</SelectItem>
                {STATUSES.map((s) => (
                  <SelectItem key={s} value={s}>{STATUS_META[s].label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="overflow-x-auto rounded-md border border-border">
          <table className="w-full text-left text-xs">
            <thead className="bg-muted/50 text-muted-foreground">
              <tr>
                <th scope="col" className="px-3 py-2 font-medium">{t('emailTrail.when', { defaultValue: 'When' })}</th>
                <th scope="col" className="px-3 py-2 font-medium">{t('emailTrail.recipient', { defaultValue: 'Recipient' })}</th>
                <th scope="col" className="px-3 py-2 font-medium">{t('emailTrail.subject', { defaultValue: 'Subject' })}</th>
                <th scope="col" className="px-3 py-2 font-medium">{t('emailTrail.status', { defaultValue: 'Outcome' })}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {rows.map((d) => {
                const meta = STATUS_META[d.status] ?? STATUS_META.deduped;
                const Icon = meta.icon;
                return (
                  <tr key={d.id}>
                    <td className="whitespace-nowrap px-3 py-2 text-muted-foreground">{formatDateTime(d.created_at)}</td>
                    <td className="px-3 py-2 text-foreground">{d.recipient_email}</td>
                    <td className="px-3 py-2">
                      <span className="text-foreground">{d.subject || '—'}</span>
                      {d.event_type && (
                        <span className="ml-2 text-muted-foreground">({d.event_type})</span>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      <Badge variant="outline" className={meta.cls} title={meta.hint}>
                        <Icon className="mr-1 h-3 w-3" aria-hidden />
                        {meta.label}
                      </Badge>
                      {/* The reason is the useful half of a non-delivery. */}
                      {d.error && (
                        <p className="mt-1 max-w-[28rem] text-[11px] text-muted-foreground">{d.error}</p>
                      )}
                    </td>
                  </tr>
                );
              })}
              {rows.length === 0 && !loading && (
                <tr>
                  <td colSpan={4} className="px-3 py-6 text-center text-muted-foreground">
                    {recipient || status !== 'all'
                      ? t('emailTrail.noneMatching', { defaultValue: 'No email activity matches those filters.' })
                      : t('emailTrail.none', { defaultValue: 'No email activity recorded yet.' })}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
};

export default EmailDeliveryTrailCard;
